import {
  ComponentLayoutThumbnail,
  ComponentOverlayLayer,
  type ExpectedComponent,
  TimingThumbnail,
} from '@cx-web/components/page-template/ComponentOverlay';
import { FrameworkOverlay } from '@cx-web/components/page-template/FrameworkOverlay';
import clsx from 'clsx';

import { type ComponentLoaderContext } from '~/types';
import { type SectionMap, type SportsPageGridSectionName } from '~/types/template';

import { renderComponents } from '../../utils/renderComponents';
import { Section } from '../Section/Section';

import styles from './SportsPageGridTemplate.module.css';

interface SportsPageGridTemplateProps {
  componentLoaderContext: ComponentLoaderContext;
  expectedComponentLayout?: ExpectedComponent[];
  gtmId: string;
  hasBannerAds: boolean;
  sections: Pick<SectionMap, SportsPageGridSectionName>;
  timings?: Record<string, number>;
}

export const SportsPageGridTemplate = ({
  componentLoaderContext,
  expectedComponentLayout,
  gtmId,
  hasBannerAds,
  sections,
  timings,
}: SportsPageGridTemplateProps) => {
  const showTop = !!sections.top;
  const showCenterTop = !!sections.centerTop;
  const showCenterBottom = !!sections.centerBottom;
  const showBottom = !!sections.bottom;

  const hasGridGroup = (sections.centerTop ?? []).some(component => component?.name === 'GRID_GROUP');

  const containerClasses = clsx('adverts-and-atlas-wrapper', {
    'adverts-and-atlas-wrapper--adless': !hasBannerAds,
  });

  return (
    <div className={containerClasses}>
      <main
        className={clsx(styles.mainWrapper, { [styles.gridGroup]: hasGridGroup })}
        id="sports-page-grid-main-tag-wrapper"
      >
        {hasBannerAds && (
          <div className="ad-left-container">
            <div id="ad-left"></div>
          </div>
        )}
        {showTop && (
          <Section
            ariaLabel="Page Header Top Section"
            content={renderComponents({
              components: sections.top ?? null,
              componentLoaderContext,
              gtmId,
              section: 'Top',
            })}
            dataTestId="sports-page-grid-top"
            id="sports-page-grid-top"
          />
        )}
        <div
          className={clsx(styles.sportsPageGridContentContainer, { [styles.gridGroup]: hasGridGroup })}
          id="sports-page-grid-content-container"
        >
          {showCenterTop && (
            <Section
              ariaLabel="Main Content Center Top Section"
              className={styles.sportsPageGridCenterTop}
              content={renderComponents({
                components: sections.centerTop ?? null,
                componentLoaderContext,
                gtmId,
                section: 'Center Top',
              })}
              dataTestId="sports-page-grid-center-top"
              id="sports-page-grid-center-top"
            />
          )}
          {showCenterBottom && (
            <Section
              ariaLabel="Main Content Center Bottom Section"
              className={styles.sportsPageGridCenterBottom}
              content={renderComponents({
                components: sections.centerBottom ?? null,
                componentLoaderContext,
                gtmId,
                section: 'Center Bottom',
              })}
              dataTestId="sports-page-grid-center-bottom"
              id="sports-page-grid-center-bottom"
            />
          )}
        </div>
        {showBottom && (
          <Section
            ariaLabel="Informational Content Bottom Section"
            className={styles.sportsPageGridBottom}
            content={renderComponents({
              components: sections.bottom ?? null,
              componentLoaderContext,
              gtmId,
              section: 'Bottom',
            })}
            dataTestId="sports-page-grid-bottom"
            id="sports-page-grid-bottom"
          />
        )}
        {hasBannerAds && (
          <div className="ad-right-container">
            <div id="ad-right"></div>
          </div>
        )}
        {componentLoaderContext.showFrameworkOverlay && <FrameworkOverlay framework="RRv7" />}
        {componentLoaderContext.showComponentOverlay && <ComponentOverlayLayer />}
        {componentLoaderContext.showComponentOverlay && expectedComponentLayout && (
          <ComponentLayoutThumbnail expectedLayout={expectedComponentLayout} />
        )}
        {timings && <TimingThumbnail timings={timings} />}
      </main>
    </div>
  );
};
